import React, { useState } from 'react';
import { ArrowRight, Shield, Users, Key, Fingerprint, CheckCircle, Lock, UserCheck } from 'lucide-react';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import ConsultationModal from '../../components/ConsultationModal';
import DemoModal from '../../components/DemoModal';

const IdentityManagementPage: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isConsultationModalOpen, setIsConsultationModalOpen] = useState(false);
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);

  const identityManagementSolutions = [
    {
      icon: Key,
      title: "Single Sign-On (SSO)",
      description: "Streamlined access management with enterprise SSO solutions supporting SAML, OAuth, and OpenID Connect.",
      features: ["SAML 2.0 support", "OAuth 2.0/OIDC", "Multi-domain SSO", "Mobile app integration"]
    },
    {
      icon: Fingerprint,
      title: "Multi-Factor Authentication",
      description: "Advanced MFA solutions with biometric, hardware tokens, and risk-based authentication.",
      features: ["Biometric authentication", "Hardware tokens", "Risk-based MFA", "Adaptive authentication"]
    },
    {
      icon: Users,
      title: "Identity Governance",
      description: "Comprehensive identity lifecycle management with automated provisioning and access reviews.",
      features: ["User provisioning", "Access reviews", "Role management", "Compliance reporting"]
    },
    {
      icon: UserCheck,
      title: "Privileged Access Management",
      description: "Secure privileged account management with session monitoring and just-in-time access.",
      features: ["Privileged sessions", "Just-in-time access", "Password vaulting", "Session recording"]
    }
  ];

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-slate-950' : 'bg-slate-50'
    }`}>
      <Header 
        isMenuOpen={isMenuOpen} 
        setIsMenuOpen={setIsMenuOpen}
        isDarkMode={isDarkMode}
        toggleDarkMode={() => setIsDarkMode(!isDarkMode)}
      />

      <ConsultationModal 
        isOpen={isConsultationModalOpen} 
        onClose={() => setIsConsultationModalOpen(false)} 
      />
      <DemoModal 
        isOpen={isDemoModalOpen} 
        onClose={() => setIsDemoModalOpen(false)} 
      />

      {/* Hero Section */}
      <section className={`relative py-20 overflow-hidden ${
        isDarkMode 
          ? 'bg-gradient-to-br from-slate-950 via-slate-900 to-rose-950 text-slate-100' 
          : 'bg-gradient-to-br from-rose-50 via-pink-50 to-red-50 text-slate-900'
      }`}>
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/1181244/pexels-photo-1181244.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop"
            alt="Identity Management"
            className="w-full h-full object-cover opacity-10"
          />
          <div className={`absolute inset-0 ${
            isDarkMode 
              ? 'bg-gradient-to-r from-slate-950/90 to-rose-950/90' 
              : 'bg-gradient-to-r from-rose-50/90 to-red-50/90'
          }`} />
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <Users className={`h-16 w-16 mx-auto mb-6 ${
              isDarkMode ? 'text-rose-400' : 'text-rose-600'
            }`} />
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Identity <span className={isDarkMode ? "text-rose-400" : "text-rose-600"}>Management</span>
            </h1>
            <p className={`text-xl md:text-2xl mb-8 max-w-3xl mx-auto ${
              isDarkMode ? "text-slate-300" : "text-slate-600"
            }`}>
              Comprehensive identity and access management solutions with SSO, MFA, 
              and privileged access controls for enterprise security.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={() => setIsConsultationModalOpen(true)}
                className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center transform hover:scale-105 shadow-lg ${
                  isDarkMode 
                    ? "bg-rose-600 hover:bg-rose-700 text-white hover:shadow-rose-500/25" 
                    : "bg-rose-600 hover:bg-rose-700 text-white hover:shadow-rose-500/25"
                }`}
              >
                <Shield className="mr-2 h-5 w-5" />
                Identity Assessment
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
              <button 
                onClick={() => setIsDemoModalOpen(true)}
                className={`px-8 py-4 rounded-lg font-semibold transition-all duration-300 border-2 ${
                  isDarkMode 
                    ? "border-rose-400 text-rose-400 hover:bg-rose-400 hover:text-slate-900" 
                    : "border-rose-600 text-rose-600 hover:bg-rose-600 hover:text-white"
                }`}
              >
                View IAM Architecture
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Identity Management Solutions Section */}
      <section className={`py-20 ${isDarkMode ? 'bg-slate-900' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
              isDarkMode ? 'text-slate-100' : 'text-slate-900'
            }`}>
              Complete Identity Management Solutions
            </h2>
            <p className={`text-xl max-w-3xl mx-auto ${
              isDarkMode ? 'text-slate-300' : 'text-slate-600'
            }`}>
              Secure identity and access management for modern enterprise environments
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {identityManagementSolutions.map((solution, index) => (
              <div key={index} className={`p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow ${
                isDarkMode ? 'bg-slate-800 border border-slate-700' : 'bg-slate-50 border border-slate-200'
              }`}>
                <solution.icon className={`h-12 w-12 mb-4 ${
                  isDarkMode ? 'text-rose-400' : 'text-rose-600'
                }`} />
                <h3 className={`text-xl font-semibold mb-3 ${
                  isDarkMode ? 'text-slate-100' : 'text-slate-900'
                }`}>{solution.title}</h3>
                <p className={`mb-4 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  {solution.description}
                </p>
                <ul className="space-y-2">
                  {solution.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center space-x-2">
                      <CheckCircle className={`h-4 w-4 ${
                        isDarkMode ? 'text-green-400' : 'text-green-600'
                      }`} />
                      <span className={`text-sm ${
                        isDarkMode ? 'text-slate-300' : 'text-slate-600'
                      }`}>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer 
        onConsultationClick={() => setIsConsultationModalOpen(true)}
        onDemoClick={() => setIsDemoModalOpen(true)}
        isDarkMode={isDarkMode}
      />
    </div>
  );
};

export default IdentityManagementPage;